import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, firstValueFrom} from "rxjs";
import {Tour, TourModel} from "./tour.model";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs, getDocsFromCache,
  query,
  setDoc,
  where
} from "@angular/fire/firestore";
import {Storage} from "@angular/fire/storage";
import {LanguageService} from "./language.service";

@Injectable({
  providedIn: 'root'
})
export class TourService {

  private apiURL = 'http://localhost:3000/tours';
  private tourCollection = collection(this.firestore, 'tours');

  private cachedTours: Tour[] = [];

  public activeTour$: BehaviorSubject<Tour> = new BehaviorSubject<Tour>(null);

  constructor(
    private http: HttpClient,
    private firestore: Firestore,
    private storage: Storage,
    private languageService: LanguageService
  ) {
    Tour.storage = storage;
    Tour.languageService = languageService;
  }

  async loadTours(forceCache: boolean = false) {
    // return collectionData(this.tourCollection, {
    //   idField: 'id',
    // }).pipe(
    //   exhaustMap((tours: DocumentData[]) => {
    //     return Promise.all(tours.map((tour: DocumentData) => {
    //       return Tour.fromJSON(tour);
    //     }));
    //   }),
    //   map((tours: Tour[]) => {
    //     return tours.sort((a: Tour, b: Tour) => {
    //       return a.step - b.step;
    //     });
    //   })
    // );

    if(this.cachedTours.length > 0) {
      console.log('Used service cache for tours');
      return this.cachedTours;
    }

    const tourDocs = await getDocsFromCache(this.tourCollection).then(result => {
      console.log('Cache found for tours', result);
      if(result.empty){
        console.log('No cache found for tours, use live one');
        return getDocs(this.tourCollection);
      }
      return result;
    }).catch((err) => {
      console.log('No cache found for tours', err);
      return getDocs(this.tourCollection);
    })

    if (tourDocs.empty) {
      console.log('No tours found');
      return [];
    }

    const tours = await Promise.all(tourDocs.docs.map((tourDoc) => {
      return Tour.fromJSON({...tourDoc.data(), id: tourDoc.id});
    }));

    this.cachedTours = tours.sort((a: Tour, b: Tour) => {
      return a.step - b.step;
    });

    return this.cachedTours;
  }

  async loadTour(id: string, setActive: boolean = false) {
    let doc = await getDoc(this.createDocRefWithId(id));
    if (!doc.exists()) {
      throw new Error('Tour not found');
    }

    const result = await Tour.fromJSON({...doc.data(), id: doc.id});
    if (setActive) {
      this.activeTour$.next(result);
    }
    return result;
  }

  async addTour(tour: TourModel) {
    delete tour["id"];

    console.log('Add tour', tour);
    const tourDoc = await addDoc(this.tourCollection, tour);

    this.cachedTours.push(await Tour.fromJSON({...tour, id: tourDoc.id}));
    return tourDoc;
    // return this.updateTour({...tour, id: result.id});
  }

  async updateTour(tour: TourModel) {
    console.log('Update tour', tour);
    if (!tour.id) {
      throw new Error('Tour has no id');
    }
    return setDoc(this.createDocRefWithId(tour.id), tour);
  }

  async getTourByStepNumber(stepNumber: number, setActive: boolean = false) {
    const q = query(this.tourCollection, where("step", "==", stepNumber));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error('Tour not found');
    }
    const result = await Tour.fromJSON({...querySnapshot.docs[0].data(), id: querySnapshot.docs[0].id});

    if (setActive) {
      this.activeTour$.next(result);
    }
    return result;
  }

  async deleteTour(id: string) {
    console.log('Delete tour', id);
    const docRef = this.createDocRefWithId(id);
    return deleteDoc(docRef).then(() => {
      this.cachedTours = this.cachedTours.filter((tour: Tour) => {
        return tour.id !== id;
      });

      console.log('Deleted tour', id);
    });
  }

  async getTourByAudioFolder(folder: string) {
    const tours = await this.loadTours(true);
    const tour = tours.find((tour: Tour) => {
      return tour.audioStorageFolder === folder;
    });
    if (!tour) {
      throw new Error('Tour not found');
    }
    return tour;
  }

  getLatestTour() {
    return this.cachedTours[this.cachedTours.length - 1];
  }

  private createDocRefWithId(id: string) {
    return doc(this.firestore, `tours/${id}`);
  }
}
